import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Text, TextInput, StyleSheet, TouchableOpacity } from 'react-native';

import Box from '@components/Restyle/Box';
import { RegisterFormValues } from '@screens/Auth/RegisterForm';
import { RegisterWithPhoneNumberFormValues } from '@screens/Auth/RegisterWithPhoneNumberForm';
import theme from '@themes/theme';

interface VerificationCodeInputProps {
  inputSize: number;
  onTextChanged: (text: string) => void;
  hasError?: boolean;
  forceSuccessState?: boolean;
  disabled?: boolean;
}

const VerificationCodeInputWeb: React.FC<VerificationCodeInputProps> = ({
  inputSize,
  onTextChanged,
  hasError = false,
  forceSuccessState = false,
  disabled = false,
}) => {
  const ITEM_SIZE = 48;
  const ITEM_GAP = 16;
  const [textString, setTextString] = useState('');
  const inputRef = useRef<TextInput>(null);

  const { values } = useFormikContext<
    RegisterFormValues | RegisterWithPhoneNumberFormValues
  >();

  const digits = useMemo(
    () =>
      Array.from({ length: inputSize }, (_, index) => textString[index] ?? ''),
    [inputSize, textString]
  );
  const activeIndex = Math.min(textString.length, inputSize - 1);

  useEffect(() => {
    if (values.verify_code == '') {
      setTextString('');
    }
  }, [values.verify_code]);

  // Handles the moment locking starts (the field may already be focused);
  // the onFocus bounce-back below handles the rest of the lock window.
  useEffect(() => {
    if (disabled) inputRef.current?.blur();
  }, [disabled]);

  const setTextInput = (text: string) => {
    const xtext = text.replace(/\D/g, '').slice(0, inputSize);

    setTextString(xtext);
    onTextChanged(xtext);
  };

  return (
    <Box style={styles.viewBox}>
      <Box
        style={{
          width: inputSize * ITEM_SIZE + Math.max(inputSize - 1, 0) * ITEM_GAP,
          height: ITEM_SIZE,
        }}>
        <Box
          style={[
            styles.textBox,
            { flexDirection: 'row', justifyContent: 'center' },
          ]}>
          {digits.map((digit, index) => (
            <TouchableOpacity
              key={index}
              activeOpacity={1}
              disabled={disabled}
              onPress={() => {
                inputRef.current?.focus();
              }}>
              <Text
                style={[
                  styles.text,
                  index === inputSize - 1 ? styles.lastText : null,
                  digit ? styles.filledText : null,
                  activeIndex === index ? styles.focusText : null,
                  forceSuccessState ? styles.successText : null,
                  hasError ? styles.errorText : null,
                  disabled ? styles.disabledText : null,
                ]}>
                {digit}
              </Text>
            </TouchableOpacity>
          ))}
        </Box>
        <TextInput
          ref={inputRef}
          testID='verification-code-input'
          style={styles.hiddenInput}
          onChangeText={setTextInput}
          underlineColorAndroid='transparent'
          maxLength={inputSize}
          autoFocus={true}
          keyboardType='numeric'
          textContentType='oneTimeCode'
          autoComplete='one-time-code'
          caretHidden={true}
          value={textString}
          editable={!disabled}
          pointerEvents={disabled ? 'none' : 'auto'}
          onFocus={() => {
            // pointerEvents='none' blocks mouse-click focus while locked;
            // this bounces back keyboard-Tab focus, which pointerEvents
            // doesn't affect.
            if (disabled) inputRef.current?.blur();
          }}
        />
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  viewBox: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: 1,
  },
  textBox: {
    position: 'absolute',
  },
  text: {
    height: 48,
    width: 48,
    lineHeight: 48,
    borderWidth: 1,
    borderRadius: 8,
    borderColor: '#e8e8e8',
    color: 'black',
    fontSize: 25,
    marginRight: 16,
    textAlign: 'center',
    backgroundColor: 'white',
  },
  lastText: {
    marginRight: 0,
  },
  filledText: {
    borderColor: theme.colors.greenSecondary,
  },
  successText: {
    borderColor: theme.colors.greenSecondary,
  },
  focusText: {
    borderColor: theme.colors.greenSecondary,
    shadowColor: theme.colors.greenSecondary,
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0.35,
    shadowRadius: 2,
  },
  errorText: {
    borderColor: theme.colors.alertRed,
    shadowColor: 'transparent',
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  disabledText: {
    backgroundColor: theme.colors.grey01,
    borderColor: theme.colors.grey02,
    color: theme.colors.grey04,
    shadowColor: 'transparent',
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  hiddenInput: {
    width: '100%',
    height: 48,
    position: 'absolute',
    left: 0,
    opacity: 0.01,
  },
});

export default VerificationCodeInputWeb;
